import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import axiosInstance from '../../utils/axios';
import { getImageUrl } from '../../utils/imageUtils';
import { Task } from '../../types';

interface TaskDetailProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  onTaskUpdated: () => void;
}

const TaskDetail: React.FC<TaskDetailProps> = ({ open, onClose, taskId, onTaskUpdated }) => {
  console.log('🎯 TaskDetail component mounted with props:', { open, taskId });
  
  const [task, setTask] = useState<Task | null>(null);
  const [individual, setIndividual] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000';

  useEffect(() => {
    if (open && taskId) {
      fetchTaskDetails();
    }
  }, [open, taskId]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tasks/${taskId}`);
      console.log('📋 Task details received:', response.data);
      setTask(response.data);
      
      // Si la tarea tiene individualId pero no individual, obtener la información del particular
      if (response.data.individualId && !response.data.individual) {
        console.log('🔍 Fetching individual details for ID:', response.data.individualId);
        try {
          const individualResponse = await axiosInstance.get(`/individuals/${response.data.individualId}`);
          console.log('👤 Individual details received:', individualResponse.data);
          setIndividual(individualResponse.data);
        } catch (individualError) {
          console.error('Error fetching individual details:', individualError);
          // No mostrar error al usuario si falla obtener detalles del individual
        }
      } else {
        console.log('❌ No individualId or individual already included');
        // Limpiar individual si la tarea no tiene individualId
        setIndividual(null);
      }
      
      setError(null);
    } catch (error: any) {
      console.error('Error fetching task details:', error);
      setError(error.response?.data?.message || 'Error al cargar los detalles de la tarea');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={fetchTaskDetails}>Reintentar</Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (!task) {
    return null;
  }

  console.log('🎯 TaskDetail render with task:', {
    id: task.id,
    company: task.company,
    individual: task.individual,
    individualId: task.individualId,
    individualState: individual
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalles de la Tarea</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Nombre de Servicio</Typography>
            <Typography>{task.location}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Fecha y Hora</Typography>
            <Typography>{new Date(task.dateTime).toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Estado</Typography>
            <Chip
              label={task.status}
              color={
                task.status === 'COMPLETED' ? 'success' :
                task.status === 'IN_PROGRESS' ? 'warning' :
                task.status === 'CANCELLED' ? 'error' : 'default'
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Cliente</Typography>
            <Typography>
              {task.company?.name ? `🏢 ${task.company.name}` : 
               task.individual?.name ? `👤 ${task.individual.name}` :
               individual?.name ? `👤 ${individual.name}` :
               task.individualId ? `👤 Particular (ID: ${task.individualId})` : 
               'No especificado'}
            </Typography>
            {(task.company?.address || task.individual?.address || individual?.address) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                📍 {task.company?.address || task.individual?.address || individual?.address}
              </Typography>
            )}
            {(task.company?.notes || task.individual?.notes || individual?.notes) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                📝 {task.company?.notes || task.individual?.notes || individual?.notes}
              </Typography>
            )}
            {/* Debug info */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Debug: company={task.company?.name || 'null'}, individual={task.individual?.name || 'null'}, individualState={individual?.name || 'null'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Responsable</Typography>
            <Typography>{task.responsible ? `${task.responsible.firstName} ${task.responsible.lastName}` : 'No asignado'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Categoría</Typography>
            <Typography>{task.category?.name || 'Sin categoría'}</Typography>
          </Grid>
          {task.notes && (
            <Grid item xs={12}>
              <Typography variant="h6">Notas</Typography>
              <Typography>{task.notes}</Typography>
            </Grid>
          )}
          {task.beforePhoto && task.beforePhoto.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6">Fotos Antes</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {task.beforePhoto.map((photo, index) => (
                  <img
                    key={index}
                    src={getImageUrl(photo, backendUrl)}
                    alt={`Foto antes ${index + 1}`}
                    style={{ width: 150, height: 150, objectFit: 'cover' }}
                  />
                ))}
              </Box>
            </Grid>
          )}
          {task.duringPhoto && task.duringPhoto.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6">Fotos Durante</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {task.duringPhoto.map((photo, index) => (
                  <img
                    key={index}
                    src={getImageUrl(photo, backendUrl)}
                    alt={`Foto durante ${index + 1}`}
                    style={{ width: 150, height: 150, objectFit: 'cover' }}
                  />
                ))}
              </Box>
            </Grid>
          )}
          {task.afterPhoto && task.afterPhoto.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6">Fotos Después</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {task.afterPhoto.map((photo, index) => (
                  <img
                    key={index}
                    src={getImageUrl(photo, backendUrl)}
                    alt={`Foto después ${index + 1}`}
                    style={{ width: 150, height: 150, objectFit: 'cover' }}
                  />
                ))}
              </Box>
            </Grid>
          )}
          {task.signature && (
            <Grid item xs={12}>
              <Box mt={4} textAlign="center">
                <Typography variant="h6">Firma</Typography>
                <img src={task.signature} alt="Firma" style={{ maxWidth: 400, border: '1px solid #ccc', borderRadius: 8 }} />
                <Box mt={2}>
                  <Button variant="outlined" color="primary" onClick={() => window.open(`/tasks/${task.id}/pdf`, '_blank')}>
                    Descargar Informe de servicio (PDF)
                  </Button>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetail; 